import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Loader2, ArrowLeft } from "lucide-react";
import { Logo } from "./Logo";
import { CALCOM_LINK } from "../config";
import { renderInlineCal } from "../lib/cal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 20;

export default function PaymentResult() {
  const isCancel = window.location.pathname.startsWith("/payment/cancel");
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking"); // checking | paid | timeout | error | cancelled

  useEffect(() => {
    if (isCancel || !sessionId) {
      if (!sessionId && !isCancel) setState("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch {
        /* keep trying */
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goHome = () => { window.location.href = "/"; };

  const showScheduler = state === "paid" && CALCOM_LINK;
  useEffect(() => {
    if (showScheduler) renderInlineCal("#cal-inline");
  }, [showScheduler]);

  const Icon = () => {
    if (state === "checking") return <Loader2 className="h-7 w-7 animate-spin text-[#b8c0cb]" />;
    if (state === "paid") return <Check className="h-8 w-8 text-white" />;
    return <X className="h-8 w-8 text-white" />;
  };

  const copy = {
    checking: { t: "Confirming your payment…", d: "Hold tight — we're verifying your transaction securely with Stripe." },
    paid: { t: "You're booked.", d: "Payment received. We'll email you and reach out within 24 hours to schedule your session." },
    timeout: { t: "Still processing…", d: "Your payment is taking a moment to confirm. If you completed checkout, you'll receive a confirmation email shortly." },
    error: { t: "Something went wrong.", d: "We couldn't confirm this payment. If you were charged, contact requests@surswa.com and we'll sort it immediately." },
    cancelled: { t: "Checkout cancelled.", d: "No charge was made. Whenever you're ready, you can book your Strategy Session again." },
  }[state];

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070708] px-6" data-testid="payment-result">
      <div className="absolute left-1/2 top-1/3 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(206,212,220,0.10),transparent_66%)] blur-2xl" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-5%,rgba(255,255,255,0.05),transparent_45%)]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`glass relative z-10 w-full rounded-3xl p-10 text-center md:p-14 ${showScheduler ? "max-w-2xl" : "max-w-lg"}`}
      >
        <button onClick={goHome} className="mx-auto mb-10 flex items-center justify-center" aria-label="SÛRSWA AI home">
          <Logo className="text-lg" />
        </button>

        <div
          className={`mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full border ${
            state === "paid" ? "border-white/20 bg-white/[0.06]" : "border-white/10 bg-white/[0.03]"
          }`}
          data-testid={`payment-icon-${state}`}
        >
          <Icon />
        </div>

        <h1 className="font-display text-3xl font-semibold tracking-tight text-metal md:text-4xl" data-testid="payment-title">
          {copy.t}
        </h1>
        <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-[#9aa2ac]">{copy.d}</p>

        {showScheduler && (
          <div className="mt-8 text-left">
            <p className="mb-3 text-center text-xs uppercase tracking-[0.2em] text-[#b8c0cb]">Pick your session time</p>
            <div id="cal-inline" style={{ minHeight: 560 }} className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]" />
          </div>
        )}

        {state !== "checking" && (
          <button
            onClick={goHome}
            data-testid="payment-home-button"
            className="group mt-10 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#070708] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Back to SÛRSWA AI
          </button>
        )}
      </motion.div>
    </div>
  );
}
